import { DateTime } from '@mode-switch/express';
import { Cond, Field, Filter, Model } from '@mode-switch/model';
import {
  EncTopByFieldCondition,
  Encoding,
  FilterType,
  RelativeDateFilterHelpers,
  SelectionMode,
} from '@mode-vegas/vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { safeStringToNumber } from '@mode/shared/util-js';
import { makeField } from './field';
import { safeMapValues } from './value';

type ModeRole = FlamingoTypes.ModeRole;

export function makeFilters(
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  fields: Map<string, Field<ModeRole>>
): Array<Filter<ModeRole>> {
  const view = Model.Views.find(model, viewName)!;
  const filters = new Array<Filter<ModeRole>>();

  for (const filter of encoding.filter) {
    const field = makeField(model, viewName, encoding, fields, filter);

    switch (filter.filterType) {
      case FilterType.Discrete:
      case FilterType.DiscreteBoolean: {
        let mode = Cond.Selection.Mode.All;

        switch (filter.selection.mode) {
          case SelectionMode.INCLUDE:
            mode = Cond.Selection.Mode.Include;
            break;
          case SelectionMode.EXCLUDE:
            mode = Cond.Selection.Mode.Exclude;
            break;
          case SelectionMode.EXCLUDE_ALL:
            mode = Cond.Selection.Mode.ExcludeAll;
            break;
        }

        if (filter.selection.mode !== SelectionMode.ALL) {
          const selected = safeMapValues(filter.selection.selected, field.formula.type);
          filters.push(
            Filter.Discrete.make(field, {
              selection: Cond.Selection.make(mode, selected),
            })
          );
        }
        break;
      }
      case FilterType.Continuous: {
        const { min, max } = filter.range;
        const hasMin = min != null && min.toString() !== '';
        const hasMax = max != null && max.toString() !== '';

        if (hasMin && hasMax) {
          filters.push(
            Filter.Continuous.make(field, {
              range: Cond.Between.make(safeStringToNumber(min), safeStringToNumber(max), {
                min: filter.includeMin,
                max: filter.includeMax,
                nulls: filter.includeNulls,
              }),
            })
          );
        } else if (hasMin) {
          filters.push(
            Filter.Continuous.make(field, {
              range: Cond.AtLeast.make(safeStringToNumber(min), {
                min: filter.includeMin,
                nulls: filter.includeNulls,
              }),
            })
          );
        } else if (hasMax) {
          filters.push(
            Filter.Continuous.make(field, {
              range: Cond.AtMost.make(safeStringToNumber(max), {
                max: filter.includeMax,
                nulls: filter.includeNulls,
              }),
            })
          );
        }
        break;
      }
      case FilterType.ContinuousDate: {
        const { min, max } = filter.range;
        const hasMin = min != null && min !== 0 && min.toString() !== '';
        const hasMax = max != null && max !== 0 && max.toString() !== '';

        if (hasMin && hasMax) {
          // min and max can be 0 or null on date ranges for whatever reason
          filters.push(
            Filter.Continuous.make(field, {
              range: Cond.Between.make(safeStringToNumber(min) * 1000, safeStringToNumber(max) * 1000, {
                min: filter.includeMin,
                max: filter.includeMax,
                nulls: filter.includeNulls,
              }),
            })
          );
        } else if (hasMin) {
          filters.push(
            Filter.Continuous.make(field, {
              range: Cond.AtLeast.make(safeStringToNumber(min) * 1000, {
                min: filter.includeMin,
                nulls: filter.includeNulls,
              }),
            })
          );
        } else if (hasMax) {
          filters.push(
            Filter.Continuous.make(field, {
              range: Cond.AtMost.make(safeStringToNumber(max) * 1000, {
                max: filter.includeMax,
                nulls: filter.includeNulls,
              }),
            })
          );
        }
        break;
      }
      case FilterType.RelativeDate: {
        // We will have relative date filters that do not have w/y start values
        // encoded in them. This is an instruction to pick up the workspace settings...
        const weekStart = filter.weekStart ?? DateTime.weekStartDow(view.weekStart);
        const yearStart = filter.yearStart ?? DateTime.yearStartMonth(view.yearStart);
        const range = RelativeDateFilterHelpers.getDateRange({
          ...filter,
          weekStart,
          yearStart,
        });

        const min = range.from ? +range.from * 1000 : null;
        const max = range.to ? +range.to * 1000 : null;

        if (min != null && max != null) {
          filters.push(
            Filter.Continuous.make(field, {
              range: Cond.Between.make(min, max, {
                nulls: filter.includeNulls,
                min: true,
                max: true,
              }),
            })
          );
        } else if (min != null) {
          filters.push(
            Filter.Continuous.make(field, {
              range: Cond.AtLeast.make(min, {
                nulls: filter.includeNulls,
                min: true,
              }),
            })
          );
        } else if (max != null) {
          filters.push(
            Filter.Continuous.make(field, {
              range: Cond.AtMost.make(max, {
                nulls: filter.includeNulls,
                max: true,
              }),
            })
          );
        }
        break;
      }
      case FilterType.TopByField: {
        const byField = makeField(model, viewName, encoding, fields, filter.cond.byField);
        let operator = Cond.TopOperator.Top;
        if (filter.cond.operator === EncTopByFieldCondition.Operator.Bottom) {
          operator = Cond.TopOperator.Bottom;
        }
        let sequence = Cond.TopSequence.FirstIsolate;
        if (filter.cond.sequence === EncTopByFieldCondition.Sequence.LastIsolate) {
          sequence = Cond.TopSequence.LastIsolate;
        }
        filters.push(
          Filter.Discrete.make(field, {
            topByField: Cond.TopByField.make(byField, operator, sequence, filter.cond.limit),
          })
        );
        break;
      }
    }
  }

  return filters;
}
